// RTS camera controller (DESIGN.md §11): WASD/MMB-drag pan, wheel zoom,
// Q/E yaw in 45° steps. Fixed pitch. Render-side; floats and Math.* allowed.

import * as THREE from 'three';

const PITCH = THREE.MathUtils.degToRad(50);
const PAN_SPEED = 24; // world units/s at dist 40
const ZOOM_MIN = 10;
const ZOOM_MAX = 90;

export class RtsCamera {
  readonly camera: THREE.PerspectiveCamera;
  target = new THREE.Vector3();
  yaw = Math.PI / 4;
  private yawGoal = Math.PI / 4;
  dist = 40;
  private keys = new Set<string>();
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };

  constructor(aspect: number, mapW: number, mapH: number) {
    this.camera = new THREE.PerspectiveCamera(50, aspect, 0.5, 600);
    this.target.set(mapW / 2, 0, mapH / 2);
    this.bounds = { w: mapW, h: mapH };
    this.apply();
  }

  private bounds = { w: 192, h: 192 };

  attach(el: HTMLElement): void {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.keys.add(e.code);
      if (e.code === 'KeyQ') this.yawGoal += Math.PI / 4;
      if (e.code === 'KeyE') this.yawGoal -= Math.PI / 4;
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.dist = THREE.MathUtils.clamp(this.dist * (e.deltaY > 0 ? 1.12 : 0.89), ZOOM_MIN, ZOOM_MAX);
    });
    el.addEventListener('pointerdown', (e) => {
      if (e.button === 1) {
        this.dragging = true;
        this.lastPointer = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    });
    window.addEventListener('pointerup', (e) => {
      if (e.button === 1) this.dragging = false;
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.lastPointer = { x: e.clientX, y: e.clientY };
      const scale = this.dist / 500;
      this.panLocal(-dx * scale, -dy * scale);
    });
  }

  private panLocal(right: number, forward: number): void {
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    this.target.x += right * cos - forward * sin;
    this.target.z += right * sin + forward * cos;
    this.target.x = THREE.MathUtils.clamp(this.target.x, 0, this.bounds.w);
    this.target.z = THREE.MathUtils.clamp(this.target.z, 0, this.bounds.h);
  }

  update(dt: number): void {
    const v = (PAN_SPEED * this.dist) / 40;
    let rt = 0;
    let fw = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) rt -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) rt += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) fw -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) fw += 1;
    if (rt !== 0 || fw !== 0) this.panLocal(rt * v * dt, fw * v * dt);
    // smooth yaw toward 45° goal
    const dyaw = this.yawGoal - this.yaw;
    this.yaw += dyaw * Math.min(1, dt * 10);
    this.apply();
  }

  private apply(): void {
    const horiz = Math.cos(PITCH) * this.dist;
    const y = Math.sin(PITCH) * this.dist;
    this.camera.position.set(
      this.target.x + Math.sin(this.yaw) * horiz,
      this.target.y + y,
      this.target.z + Math.cos(this.yaw) * horiz,
    );
    this.camera.lookAt(this.target);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
